import { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useDebounce } from '../../../hooks/useDebounce';
import { usePopover } from '../../../hooks/usePopover';
import { cn } from '../../../utils/cn';
import { formatDate, toISODate } from '../../../utils/date';
import { Checkbox } from '../../ui/Checkbox/Checkbox';
import { DatePicker } from '../../ui/DatePicker/DatePicker';
import { Icon } from '../../ui/Icon/Icon';
import { Portal } from '../../ui/Portal/Portal';
import { Spinner } from '../../ui/Spinner/Spinner';
import type { ReactNode } from 'react';
import type { Option } from '../../../types/common';
import type { Column } from '../../../types/table';

/** Shared popover shell: trigger button + floating panel. */
function useMenu() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // Clicks inside another pop-up (e.g. the calendar of a date field in this menu) do not close it.
  useClickOutside([anchorRef, popRef], (e) => !(e.target as HTMLElement).closest?.('[data-popup]') && setOpen(false), open);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      anchorRef.current?.querySelector('button')?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return { open, setOpen, anchorRef, popRef };
}

/** The "Date ▾" style button. Shows the chosen value and an × to clear it when active. */
type MenuState = ReturnType<typeof useMenu>;

function Trigger({ menu, icon, label, valueLabel, onClear }: { menu: MenuState; icon?: string; label: ReactNode; valueLabel?: ReactNode; onClear: () => void }) {
  const active = !!valueLabel;
  return (
    <span ref={menu.anchorRef} className={cn('inline-flex h-9 items-stretch overflow-hidden rounded-lg border text-sm shadow-soft', active ? 'border-brand-300 bg-brand-50' : 'border-line bg-white')}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={menu.open}
        onClick={() => menu.setOpen((o) => !o)}
        className={cn('flex items-center gap-1.5 pl-3 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel', active ? 'pr-1.5 text-brand-700' : 'pr-2.5 text-navy-700 hover:bg-navy-50')}
      >
        {icon && <Icon name={icon} size={15} className={active ? 'text-brand-600' : 'text-ink-400'} />}
        <span className={cn(active && 'text-ink-500')}>{label}</span>
        {active && <span className="max-w-[10rem] truncate font-medium">{valueLabel}</span>}
        {!active && <Icon name="chevronDown" size={14} className="text-ink-400" />}
      </button>
      {active && (
        <button type="button" aria-label={`Clear ${label} filter`} onClick={onClear} className="flex items-center px-1.5 text-brand-600 outline-none hover:bg-brand-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel">
          <Icon name="x" size={14} />
        </button>
      )}
    </span>
  );
}

function Panel({ menu, width = 240, children, deps = [] }: { menu: MenuState; width?: number; children?: ReactNode; deps?: unknown[] }) {
  const style = usePopover(menu.open, menu.anchorRef, menu.popRef, { deps });
  if (!menu.open) return null;
  return (
    <Portal>
      <div ref={menu.popRef} role="dialog" style={{ ...style, width }} className="rounded-xl border border-line bg-white p-1 shadow-pop animate-pop">
        {children}
      </div>
    </Portal>
  );
}

function OptionRow({ selected, onClick, children, description }: { selected?: boolean; onClick: () => void; children?: ReactNode; description?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm outline-none hover:bg-navy-50 focus-visible:bg-navy-50', selected && 'font-medium text-brand-700')}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {description && <span className="block truncate text-xs font-normal text-ink-400">{description}</span>}
      </span>
      {selected && <Icon name="check" size={15} className="shrink-0 text-brand-600" />}
    </button>
  );
}

/**
 * Dropdown filter for one value.
 * Static: options [{ value, label }]. Remote: fetcher(search) -> records, getLabel(record).
 * valueLabel: text for the chosen value when options aren't loaded (remote filters).
 */
export interface FilterMenuProps {
  label: string;
  icon?: string;
  value?: string;
  valueLabel?: string;
  onChange: (value: string, label?: string) => void;
  options?: Option[];
  fetcher?: (search: string) => Promise<any[]>;
  getLabel?: (row: any) => string;
  getDescription?: (row: any) => ReactNode;
  searchPlaceholder?: string;
}

export function FilterMenu({ label, icon, value, valueLabel, onChange, options, fetcher, getLabel = (r) => r.name, getDescription, searchPlaceholder = 'Search' }: FilterMenuProps) {
  const menu = useMenu();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const q = useDebounce(search, 250);
  const remote = !!fetcher;
  const searchable = remote || (options?.length ?? 0) > 8;

  useEffect(() => {
    if (!menu.open || !remote) return undefined;
    let alive = true;
    setLoading(true);
    fetcher(q)
      .then((r) => alive && setRows(r))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu.open, q]);
  useEffect(() => {
    if (menu.open) setSearch('');
  }, [menu.open]);

  const list = remote
    ? rows.map((r) => ({ value: r._id, label: getLabel(r), description: getDescription?.(r) }))
    : (options ?? []).filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase()));
  const chosen = value ? valueLabel ?? options?.find((o) => String(o.value) === String(value))?.label ?? 'Selected' : '';
  const pick = (o) => {
    onChange(o.value, o.label);
    menu.setOpen(false);
  };

  return (
    <>
      <Trigger menu={menu} icon={icon} label={label} valueLabel={chosen} onClear={() => onChange('', '')} />
      <Panel menu={menu} width={remote ? 280 : 230} deps={[list.length, loading]}>
        {searchable && (
          <div className="relative mb-1 p-1">
            <Icon name="search" size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="h-9 w-full rounded-lg bg-navy-50 pl-8 pr-8 text-sm outline-none focus:ring-2 focus:ring-steel/30"
            />
            {loading && <Spinner size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-400" />}
          </div>
        )}
        <div className="max-h-72 overflow-y-auto">
          {list.length === 0 && !loading && <p className="px-3 py-5 text-center text-sm text-ink-400">Nothing found</p>}
          {list.map((o) => (
            <OptionRow key={o.value} selected={String(o.value) === String(value)} onClick={() => pick(o)} description={o.description}>
              {o.label}
            </OptionRow>
          ))}
        </div>
      </Panel>
    </>
  );
}

const iso = (d: Date) => toISODate(d);
function presets(): [key: string, label: string, from: string, to: string][] {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = (offset: number) => new Date(y, m, now.getDate() + offset);
  const fyStart = new Date(m >= 3 ? y : y - 1, 3, 1);
  return [
    ['today', 'Today', iso(now), iso(now)],
    ['7d', 'Last 7 days', iso(day(-6)), iso(now)],
    ['30d', 'Last 30 days', iso(day(-29)), iso(now)],
    ['month', 'This month', iso(new Date(y, m, 1)), iso(now)],
    ['lastMonth', 'Last month', iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))],
    ['fy', 'This financial year', iso(fyStart), iso(now)],
  ];
}

/** Date range dropdown with quick presets and a custom range. value = { from, to }. */
export interface DateRangeValue {
  from: string;
  to: string;
}

export function DateFilterMenu({ label = 'Date', value, onChange }: { label?: string; value: DateRangeValue; onChange: (v: DateRangeValue) => void }) {
  const menu = useMenu();
  const list = presets();
  const match = list.find(([, , f, t]) => f === value.from && t === value.to);
  const valueLabel = !value.from && !value.to ? '' : match ? match[1] : [value.from && formatDate(value.from), value.to && formatDate(value.to)].filter(Boolean).join(' – ');

  return (
    <>
      <Trigger menu={menu} icon="calendar" label={label} valueLabel={valueLabel} onClear={() => onChange({ from: '', to: '' })} />
      <Panel menu={menu} width={280}>
        {list.map(([key, text, from, to]) => (
          <OptionRow key={key} selected={match?.[0] === key} onClick={() => {
              onChange({ from, to });
              menu.setOpen(false);
            }}>
            {text}
          </OptionRow>
        ))}
        <div className="mt-1 space-y-2 border-t border-line p-2">
          <p className="text-xs font-medium text-ink-400">Custom range</p>
          <DatePicker size="sm" aria-label="From date" placeholder="From" value={value.from} max={value.to || undefined} onChange={(v) => onChange({ ...value, from: v ?? '' })} />
          <DatePicker size="sm" aria-label="To date" placeholder="To" value={value.to} min={value.from || undefined} onChange={(v) => onChange({ ...value, to: v ?? '' })} />
        </div>
      </Panel>
    </>
  );
}

/** "Columns" menu: tick which table columns are visible. */
export function ColumnsMenu({ columns, hidden, onChange }: { columns: Column[]; hidden: string[]; onChange: (hidden: string[]) => void }) {
  const menu = useMenu();
  return (
    <>
      <span ref={menu.anchorRef} className="inline-flex">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={menu.open}
          onClick={() => menu.setOpen((o) => !o)}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-sm text-navy-700 shadow-soft outline-none hover:bg-navy-50 focus-visible:ring-2 focus-visible:ring-steel"
        >
          <Icon name="columns" size={15} className="text-ink-400" />
          Columns
        </button>
      </span>
      <Panel menu={menu} width={220}>
        <div className="space-y-1 p-2">
          {columns.map((c, i) => (
            <Checkbox
              key={c.header}
              label={c.header}
              disabled={i === 0}
              checked={!hidden.includes(c.header)}
              onChange={(on) => onChange(on ? hidden.filter((h) => h !== c.header) : [...hidden, c.header])}
            />
          ))}
        </div>
      </Panel>
    </>
  );
}
